import { useState, useEffect } from 'react';
import { auth, db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import './admin.css';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import '../../components/Carousel/carousel.css';
import '../../pages/awards/awards.css';
import imageCompression from 'browser-image-compression';

const compressionOptions = {
    maxSizeMB: 0.1,          // Target size under 200KB
    maxWidthOrHeight: 500,  // Resize large images
    useWebWorker: true,
    fileType: 'image/webp' 
};

const cacheMetadata = { 
    cacheControl: 'public,max-age=31536000',
    contentType: 'image/webp' // Ensure the browser knows it's a WebP
};

const Admin = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loginData, setLoginData] = useState({ email: '', password: '' });

    // Data States
    const [content, setContent] = useState(null);
    const [awards, setAwards] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [newTesti, setNewTesti] = useState({ name: '', occupation: '', testimonial: '', profile_pic_url: '' });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (currentUser) fetchAllData();
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const fetchAllData = async () => {
        try {
            // Fetch Content
            const contentSnap = await getDocs(collection(db, "content"));
            if (!contentSnap.empty) {
                setContent({ id: contentSnap.docs[0].id, ...contentSnap.docs[0].data() });
            }

            // Fetch Awards - FORCE OBJECT STRUCTURE
            const awardsSnap = await getDocs(collection(db, "awards"));
            const awardsObj = {};
            awardsSnap.docs.forEach(doc => {
                // This ensures document "1" maps to awards['1']
                awardsObj[doc.id] = { ...doc.data() }; 
            });
            setAwards(awardsObj); // Now console will show awards as an Object {}

            // Fetch Testimonials
            const testiSnap = await getDocs(collection(db, "testimonials"));
            const testiData = testiSnap.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            }));

            // Sort by createdAt date (newest first)
            testiData.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
            
            setTestimonials(testiData);
        } catch (err) {
            console.error("Fetch error:", err);
        }
    };

    const handleUpdateContent = async (e) => {
        e.preventDefault();
        await updateDoc(doc(db, "content", content.id), content);
        alert("Content Updated!");
    };

    const handleUpdateAward = async (id, updatedData) => {
        await updateDoc(doc(db, "awards", id), updatedData);
        alert(`Award ${id} Updated!`);
    };

    const handleAddTestimonial = async (e) => {
        e.preventDefault();
        await addDoc(collection(db, "testimonials"), { ...newTesti, createdAt: new Date() });
        setNewTesti({ name: '', occupation: '', testimonial: '', profile_pic_url: '' });
        fetchAllData();
    };

    const handleDeleteTestimonial = async (id) => {
        if (window.confirm("Delete this testimonial?")) {
            await deleteDoc(doc(db, "testimonials", id));
            fetchAllData();
        }
    };

    const handleTestimonialImageUpload = async (e, testiId) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            // Testimonial pics are small circles, so 400px is plenty
            const testiOptions = { ...compressionOptions, maxWidthOrHeight: 400 };
            const compressedFile = await imageCompression(file, testiOptions);
            
            const storageRef = ref(storage, `testimonials/user_${testiId}_${Date.now()}`);
            const snapshot = await uploadBytes(storageRef, compressedFile, cacheMetadata);
            const downloadURL = await getDownloadURL(snapshot.ref);

            setTestimonials(prev => prev.map(t => 
                t.id === testiId ? { ...t, profile_pic_url: downloadURL } : t
            ));
            setLoading(false);
        } catch (error) {
            setLoading(false);
            alert("Testimonial upload failed: " + error.message);
        }
    };

    const handleUpdateTestimonial = async (id, updatedData) => {
        try {
            await updateDoc(doc(db, "testimonials", id), updatedData);
            alert("Testimonial updated!");
            fetchAllData();
        } catch (error) {
            alert("Error: " + error.message);
        }
    };

    const handleContentImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            // Compress before uploading
            const compressedFile = await imageCompression(file, compressionOptions);
            
            const storageRef = ref(storage, `content/profile_main_${Date.now()}`);
            const snapshot = await uploadBytes(storageRef, compressedFile, cacheMetadata);
            const downloadURL = await getDownloadURL(snapshot.ref);

            setContent({ ...content, image_url: downloadURL });
            setLoading(false);
        } catch (error) {
            setLoading(false);
            alert("Profile compression/upload failed: " + error.message);
        }
    };

    const handleImageUpload = async (e, awardId) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            // Resize and compress for small award icons
            const awardOptions = { ...compressionOptions, maxWidthOrHeight: 500 }; 
            const compressedFile = await imageCompression(file, awardOptions);
            
            const storageRef = ref(storage, `awards/award_${awardId}_${Date.now()}`);
            const snapshot = await uploadBytes(storageRef, compressedFile, cacheMetadata);
            const downloadURL = await getDownloadURL(snapshot.ref);

            setAwards(prev => ({
                ...prev,
                [awardId]: { ...prev[awardId], image_url: downloadURL }
            }));
            setLoading(false);
        } catch (error) {
            setLoading(false);
            alert(`Award upload failed: ${error.message}`);
        }
    };

    if (loading) return <div className="admin_loader">Loading Dashboard...</div>;

    if (!user) {
        return (
            <div className="admin_login_wrapper">
                <form className="admin_login_card" onSubmit={async (e) => {
                    e.preventDefault();
                    try { await signInWithEmailAndPassword(auth, loginData.email, loginData.password); } 
                    catch (err) { alert(err.message); }
                }}>
                    <h1>CMS Access</h1>
                    <input type="email" placeholder="Email" onChange={e => setLoginData({...loginData, email: e.target.value})} />
                    <input type="password" placeholder="Password" onChange={e => setLoginData({...loginData, password: e.target.value})} />
                    <button type="submit" className="primary_btn">Login</button>
                </form>
            </div>
        );
    }

    return (
        <div className="admin_all_in_one">
            <aside className="admin_sticky_nav">
                <h2>Admin Panel</h2>
                <a href="#content_section">Content</a>
                <a href="#awards_section">Awards</a>
                <a href="#testimonials_section">Testimonials</a>
                <button onClick={() => signOut(auth)} className="logout_btn">Logout</button>
            </aside>

            <main className="admin_container">
                {/* SECTION 1: CONTENT */}
                <section id="content_section" className="admin_block" style={{ marginBottom: '5rem', display: 'flex', flexDirection: 'column'}}>
                    <h2>1. Profile & Contact Details</h2>
                    
                    {content && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                            {/* MAIN CONTENT CARD */}
                            <div style={{ 
                                padding: '40px', 
                                width: '90%', 
                                maxWidth: '900px',
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '30px',
                                flexWrap: 'wrap',
                                justifyContent: 'center'
                            }}>
                                
                                {/* LEFT: IMAGE UPLOAD */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                                    <label htmlFor="content-image-upload" style={{ cursor: 'pointer' }}>
                                        <img 
                                            src={content.image_url || 'https://via.placeholder.com/300'} 
                                            style={{ 
                                                width: '250px', 
                                                height: '350px', 
                                                borderRadius: '30px', 
                                                objectFit: 'cover',
                                                backgroundColor: '#588157',
                                                border: '2px solid #3A5A40'
                                            }} 
                                            alt='Profile Preview' 
                                        />
                                    </label>
                                    <input 
                                        id="content-image-upload" 
                                        type="file" 
                                        style={{ display: 'none' }} 
                                        onChange={handleContentImageUpload} 
                                    />
                                    <span style={{ fontSize: '12px', color: '#3A5A40', fontWeight: 'bold' }}>Click Image to Change</span>
                                </div>

                                {/* RIGHT: TEXT FIELDS */}
                                <div style={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '15px'}}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '14px', fontWeight: 'bold', color: '#3A5A40' }}>Display Name</label>
                                        <input 
                                            style={{ padding: '10px', borderRadius: '10px', border: '1px solid #ccc', fontSize: '20px', fontWeight: 'bold', color:'black'  }}
                                            value={content.name} 
                                            onChange={e => setContent({...content, name: e.target.value})} 
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '14px', fontWeight: 'bold', color: '#3A5A40' }}>Designation</label>
                                        <input 
                                            style={{ padding: '10px', borderRadius: '10px', border: '1px solid #ccc', fontSize: '20px', fontWeight: 'bold', color:'black'  }}
                                            value={content.designation} 
                                            onChange={e => setContent({...content, designation: e.target.value})} 
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Contact Number</label>
                                            <input 
                                                style={{ padding: '8px', borderRadius: '8px', border: '1px solid #ccc', color:'black'  }}
                                                value={content.contact_number} 
                                                onChange={e => setContent({...content, contact_number: e.target.value})} 
                                            />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Instagram</label>
                                            <input 
                                                style={{ padding: '8px', borderRadius: '8px', border: '1px solid #ccc', color:'black'  }}
                                                value={content.instagram} 
                                                onChange={e => setContent({...content, instagram: e.target.value})} 
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Email Address</label>
                                        <input 
                                            style={{ padding: '8px', borderRadius: '8px', border: '1px solid #ccc', color:'black'  }}
                                            value={content.email} 
                                            onChange={e => setContent({...content, email: e.target.value})} 
                                        />
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Bio Line 1 ({content.desc1.length}/150)</label>
                                        <textarea 
                                            maxLength="150" 
                                            style={{ padding: '10px', borderRadius: '10px', border: '1px solid #ccc', height: '80px', resize: 'none', color:'black'  }}
                                            value={content.desc1} 
                                            onChange={e => setContent({...content, desc1: e.target.value})} 
                                        />
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Bio Line 2 ({content.desc2.length}/300)</label>
                                        <textarea 
                                            maxLength="300" 
                                            style={{ padding: '10px', borderRadius: '10px', border: '1px solid #ccc', height: '120px', resize: 'none', color:'black'  }}
                                            value={content.desc2} 
                                            onChange={e => setContent({...content, desc2: e.target.value})} 
                                        />
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Sign Off</label>
                                        <input 
                                            style={{ padding: '8px', borderRadius: '8px', border: '1px solid #ccc', color:'black' }}
                                            value={content.sign_off || ""} 
                                            placeholder="e.g. Warmest Regards,"
                                            onChange={e => setContent({...content, sign_off: e.target.value})} 
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Disclaimer Link</label>
                                        <input 
                                            style={{ padding: '8px', borderRadius: '8px', border: '1px solid #ccc', color:'black' }}
                                            value={content.disclaimer_link || ""} 
                                            placeholder="https://..."
                                            onChange={e => setContent({...content, disclaimer_link: e.target.value})} 
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* SAVE BUTTON (Sits outside the card) */}
                            <button 
                                onClick={handleUpdateContent} 
                                style={{ 
                                    marginTop: '2rem',
                                    padding: '15px 60px', 
                                    borderRadius: '40px', 
                                    backgroundColor: '#3A5A40', 
                                    color: 'white', 
                                    fontSize: '18px', 
                                    fontWeight: 'bold', 
                                    border: 'none', 
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                                    width:'20rem'
                                }}
                            >
                                Save All Profile Changes
                            </button>
                        </div>
                    )}
                </section>

                {/* SECTION 2: AWARDS (FIXED SLOTS 1, 2, 3) */}
                <section id="awards_section" className="admin_block">
                    <h2>2. Awards</h2>
                    <div className='awards__description'>Click on icons to upload; edit text directly on cards</div>
                    
                    <div className='awards__content'>
                        <div className='awards__text' style={{ flexWrap: 'wrap', gap: '1rem' }}>
                            
                            {/* --- SLOT 1 --- */}
                            <div className="awards__container" style={{ paddingBottom: '1rem', minHeight: 'auto', marginBottom: '1rem' }}>
                                <label htmlFor="upload-1" style={{ cursor: 'pointer' }}>
                                    {/* Accessing document ID "1" */}
                                    <img src={awards['1']?.image_url || 'https://via.placeholder.com/150'} className='awards__img' alt='Award 1' />
                                </label>
                                <input id="upload-1" type="file" style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, "1")} />
                                
                                <div className='indiv__section'>
                                    <input 
                                        className="indiv__awards" 
                                        style={{ fontWeight: "bold", background: 'transparent', border: 'none', textAlign: 'center', width: '90%', marginTop:'-2rem', marginBottom:'-1rem', color:'black' }}
                                        value={awards['1']?.title || ''} 
                                        onChange={(e) => setAwards({
                                            ...awards,
                                            ['1']: { ...awards['1'], title: e.target.value }
                                        })} 
                                    />
                                    <textarea 
                                        className="indiv__text" 
                                        style={{ background: 'transparent', border: 'none', textAlign: 'center', height: '30px', marginTop: '5px', width:'72%' }}
                                        value={awards['1']?.description || ''}
                                        onChange={(e) => setAwards({
                                            ...awards,
                                            ['1']: { ...awards['1'], description: e.target.value }
                                        })}
                                    />
                                </div>
                                <button onClick={() => handleUpdateAward("1", awards['1'])} className="save_btn_small" style={{ width: '80%', marginTop: '10px' }}>Save Slot 1</button>
                            </div>

                            {/* --- SLOT 2 (MIDDLE) --- */}
                            <div className="awards__container__middle" style={{ paddingBottom: '1rem', minHeight: 'auto', marginBottom: '1rem' }}>
                                <label htmlFor="upload-2" style={{ cursor: 'pointer' }}>
                                    {/* Accessing document ID "2" */}
                                    <img src={awards['2']?.image_url || 'https://via.placeholder.com/150'} className='awards__img' alt='Award 2' style={{ height: "5rem" }} />
                                </label>
                                <input id="upload-2" type="file" style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, "2")} />
                                
                                <div className='indiv__section'>
                                    <input 
                                        className="indiv__awards" 
                                        style={{ fontWeight: "bold", background: 'transparent', border: 'none', textAlign: 'center', width: '90%', color:'black' }}
                                        value={awards['2']?.title || ''} 
                                        onChange={(e) => setAwards({
                                            ...awards,
                                            ['2']: { ...awards['2'], title: e.target.value }
                                        })}
                                    />
                                    <textarea 
                                        className="indiv__text" 
                                        style={{ background: 'transparent', border: 'none', textAlign: 'center', height: '60px', marginTop: '5px', width:"75%", color: 'black' }}
                                        value={awards['2']?.description || ''}
                                        onChange={(e) => setAwards({
                                            ...awards,
                                            ['2']: { ...awards['2'], description: e.target.value }
                                        })}
                                    />
                                </div>
                                <button onClick={() => handleUpdateAward("2", awards['2'])} className="save_btn_small" style={{ width: '80%', marginTop: '10px' }}>Save Slot 2</button>
                            </div>

                            {/* --- SLOT 3 --- */}
                            <div className="awards__container" style={{ paddingBottom: '1rem', minHeight: 'auto', marginBottom: '1rem' }}>
                                <label htmlFor="upload-3" style={{ cursor: 'pointer' }}>
                                    {/* Accessing document ID "3" */}
                                    <img src={awards['3']?.image_url || 'https://via.placeholder.com/150'} className='awards__img' alt='Award 3' />
                                </label>
                                <input id="upload-3" type="file" style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, "3")} />
                                
                                <div className='indiv__section'>
                                    <input 
                                        className="indiv__awards" 
                                        style={{ fontWeight: "bold", background: 'transparent', border: 'none', textAlign: 'center', width: '90%', marginTop: '-2rem', marginBottom: '-1rem', color: 'black' }}
                                        value={awards['3']?.title || ''} 
                                        onChange={(e) => setAwards({
                                            ...awards,
                                            ['3']: { ...awards['3'], title: e.target.value }
                                        })}
                                    />
                                    <textarea 
                                        className="indiv__text" 
                                        style={{ background: 'transparent', border: 'none', textAlign: 'center', height: '30px', marginTop: '5px', width: '72%' }}
                                        value={awards['3']?.description || ''}
                                        onChange={(e) => setAwards({
                                            ...awards,
                                            ['3']: { ...awards['3'], description: e.target.value }
                                        })}
                                    />
                                </div>
                                <button onClick={() => handleUpdateAward("3", awards['3'])} className="save_btn_small" style={{ width: '80%', marginTop: '10px' }}>Save Slot 3</button>
                            </div>

                        </div>
                    </div>
                </section>

                {/* SECTION 3: TESTIMONIALS LIVE CAROUSEL EDIT */}
                <section id="testimonials_section" className="admin_block">
                    <h2>3.Testimonials Carousel</h2>
                    
                    <div style={{ width: '100%', marginTop: '2rem' }}>
                        <AliceCarousel mouseTracking disableDotsControls>
                            {testimonials.map((t) => (
                            <div 
                                key={t.id} 
                                style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                padding: '20px' 
                                }}
                            >
                                {/* THE MAIN TESTIMONIAL BOX (Beige Card Style) */}
                                <div 
                                style={{ 
                                    borderRadius: '40px',      // Large rounded corners
                                    border: '1px solid #707070', // Subtle dark border
                                    padding: '50px 40px',
                                    width: '90%',
                                    maxWidth: '550px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    boxSizing: 'border-box'
                                }}
                                >
                                {/* IMAGE UPLOAD CONTAINER */}
                                <label 
                                    htmlFor={`testi-pic-${t.id}`} 
                                    style={{ cursor: 'pointer', marginBottom: '15px' }}
                                >
                                    <img 
                                    src={t.profile_pic_url || 'https://via.placeholder.com/150'} 
                                    style={{ 
                                        width: '120px', 
                                        height: '120px', 
                                        borderRadius: '50%', 
                                        objectFit: 'cover',
                                        border: '1px solid #707070' 
                                    }} 
                                    alt='profile' 
                                    />
                                </label>
                                <input 
                                    id={`testi-pic-${t.id}`}
                                    type="file" 
                                    style={{ display: 'none' }} 
                                    onChange={(e) => handleTestimonialImageUpload(e, t.id)} 
                                />
                                
                                {/* NAME INPUT */}
                                <input 
                                    style={{ 
                                    background: 'transparent', 
                                    border: 'none', 
                                    textAlign: 'center', 
                                    width: '100%', 
                                    outline: 'none',
                                    fontSize: '26px',
                                    fontWeight: 'bold',
                                    color: 'black',
                                    marginBottom: '5px'
                                    }}
                                    value={t.name}
                                    placeholder='Name'
                                    onChange={(e) => {
                                    const next = [...testimonials];
                                    next.find(item => item.id === t.id).name = e.target.value;
                                    setTestimonials(next);
                                    }}
                                />

                                {/* OCCUPATION INPUT */}
                                <input 
                                    style={{ 
                                    background: 'transparent', 
                                    border: 'none', 
                                    textAlign: 'center', 
                                    width: '100%', 
                                    outline: 'none',
                                    fontSize: '18px',
                                    color: '#696984', // Purple-ish color from your screenshot
                                    marginBottom: '20px'
                                    }}
                                    placeholder='Occupation'
                                    value={t.occupation}
                                    onChange={(e) => {
                                    const next = [...testimonials];
                                    next.find(item => item.id === t.id).occupation = e.target.value;
                                    setTestimonials(next);
                                    }}
                                />

                                {/* TESTIMONIAL TEXTAREA */}
                                <textarea 
                                    style={{ 
                                    background: 'transparent', 
                                    border: 'none', 
                                    width: '100%', 
                                    minHeight: '150px', 
                                    resize: 'none', 
                                    outline: 'none', 
                                    padding: '0',
                                    textAlign: 'center',
                                    fontSize: '18px',
                                    lineHeight: '1.6',
                                    color: '#222',
                                    fontFamily: 'inherit'
                                    }}
                                    placeholder='Testimonial'
                                    value={t.testimonial}
                                    onChange={(e) => {
                                    const next = [...testimonials];
                                    next.find(item => item.id === t.id).testimonial = e.target.value;
                                    setTestimonials(next);
                                    }}
                                />
                                </div>

                                {/* ACTIONS SECTION: BUTTONS (Sitting outside the beige box) */}
                                <div 
                                style={{ 
                                    display: 'flex', 
                                    gap: '2rem', 
                                    marginTop: '30px', 
                                    justifyContent: 'center' 
                                }}
                                >
                                <button 
                                    onClick={() => handleUpdateTestimonial(t.id, t)} 
                                    style={{ 
                                    padding: '12px 30px', 
                                    borderRadius: '30px', 
                                    border: 'none', 
                                    backgroundColor: '#588157', 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    cursor: 'pointer',
                                    minWidth: '150px'
                                    }}
                                >
                                    Save Changes
                                </button>
                                <button 
                                    onClick={() => handleDeleteTestimonial(t.id)} 
                                    style={{ 
                                    padding: '12px 30px', 
                                    borderRadius: '30px', 
                                    border: '1px solid #d9534f', 
                                    backgroundColor: 'transparent', 
                                    color: '#d9534f', 
                                    fontWeight: 'bold', 
                                    cursor: 'pointer',
                                    minWidth: '150px'
                                    }}
                                >
                                    Delete
                                </button>
                                </div>
                            </div>
                            ))}
                        </AliceCarousel>
                    </div>

                    {/* ADD NEW TESTIMONIAL FORM */}
                    <div className="admin_card" style={{ marginTop: '3rem' }}>
                    <h3>+ Add New Testimonial</h3>
                    <form className="form_layout" onSubmit={handleAddTestimonial}>
                        <div className="input_grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            {/* NAME INPUT WITH LIMIT */}
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <input 
                                    value={newTesti.name} 
                                    placeholder="Name" 
                                    maxLength="20"
                                    onChange={e => setNewTesti({...newTesti, name: e.target.value})} 
                                    required 
                                    style={{ color: 'black', width: '100%' }}
                                />
                                <span style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                                    {newTesti.name.length}/20
                                </span>
                            </div>

                            {/* OCCUPATION INPUT WITH LIMIT */}
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <input 
                                    value={newTesti.occupation} 
                                    placeholder="Occupation" 
                                    maxLength="25"
                                    onChange={e => setNewTesti({...newTesti, occupation: e.target.value})} 
                                    required 
                                    style={{ color: 'black', width: '100%' }}
                                />
                                <span style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                                    {newTesti.occupation.length}/25
                                </span>
                            </div>
                        </div>

                        {/* TESTIMONIAL TEXTAREA WITH LIMIT */}
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                            <textarea 
                                value={newTesti.testimonial} 
                                placeholder="Write testimonial here..." 
                                maxLength="800"
                                onChange={e => setNewTesti({...newTesti, testimonial: e.target.value})} 
                                required 
                                style={{ color: 'black', height: '150px' }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#3A5A40', marginTop: '5px', textAlign: 'right' }}>
                                Character Count: {newTesti.testimonial.length}/800
                            </span>
                        </div>

                        <button type="submit" className="save_btn">Add to Carousel</button>
                    </form>
                </div>
                </section>
            </main>
        </div>
    );
};

export default Admin;